import { type ReactElement, useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';

import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import { DealChatPanel } from '../../ai-chat/components/ChatPanel.js';
import {
  getDocumentDetailQueryKey,
  updateDocument,
  type DocumentStatus,
  type DocumentType,
} from '../api/documents.client.js';
import { useDocumentDetail } from '../hooks/useDocuments.js';
import { useContacts } from '../../contacts/hooks/useContacts.js';
import { useDeals } from '../../deals/hooks/useDeals.js';
import type { ContactItem } from '../../contacts/api/contacts.client.js';
import type { DealListItem } from '../../deals/api/deals.client.js';
import { Link } from 'react-router-dom';
import { Button, Card, ConfigProvider, Form, Input, InputNumber, Typography, Spin, Select, Tag } from 'antd';

const { Title, Text } = Typography;

interface DocumentDetailsFormValues {
  title?: string;
  type?: 'contract' | 'invoice' | 'act';
  status?: 'draft' | 'sent' | 'signed' | 'cancelled';
  amount?: number | null;
  contactId?: string | null;
  dealId?: string | null;
}

export interface DocumentCardPageProps {
  documentId: string;
}

export function DocumentCardPage({ documentId }: DocumentCardPageProps): ReactElement {
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const queryClient = useQueryClient();

  const { document, isLoading, isError, error, refetch } = useDocumentDetail(documentId);
  const [bodyText, setBodyText] = useState('');
  
  // Contacts and Deals for Selects
  const { data: contacts } = useContacts({});
  const { deals } = useDeals({});

  useEffect(() => {
    if (document) {
      setBodyText(document.body);
    }
  }, [document]);

  const updateMutation = useMutation({
    mutationFn: (input: { title?: string; type?: DocumentType; status?: DocumentStatus; amount?: number | null; contactId?: string | null; dealId?: string | null; body?: string }) =>
      updateDocument(documentId, input),
    onSuccess: async () => {
      const queryKey = getDocumentDetailQueryKey(workspaceId, documentId);
      await queryClient.invalidateQueries({ queryKey, refetchType: 'none' });
      await queryClient.refetchQueries({ queryKey, type: 'active' });
    },
  });

  if (isLoading) {
    return (
      <main className="mx-auto flex items-center justify-center w-full max-w-5xl p-6" style={{ height: '80vh' }}>
        <Spin size="large" />
        <Text style={{ marginLeft: 16 }}>Загрузка документа...</Text>
      </main>
    );
  }

  if (isError || !document) {
    return (
      <main className="mx-auto flex w-full max-w-4xl p-6 items-center justify-center" style={{ height: '80vh' }}>
        <Card style={{ textAlign: 'center', maxWidth: 480, width: '100%' }}>
          <Title level={4}>Ошибка загрузки</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
            {error instanceof Error ? error.message : 'Не удалось загрузить документ'}
          </Text>
          <Button type="primary" onClick={() => void refetch()}>Повторить</Button>
        </Card>
      </main>
    );
  }

  const handleDetailsSave = (values: DocumentDetailsFormValues): void => {
    updateMutation.mutate({
      title: values.title?.trim() ?? document.title,
      type: values.type ?? document.type,
      status: values.status ?? document.status,
      amount: values.amount ?? null,
      contactId: values.contactId ?? null,
      dealId: values.dealId ?? null,
    });
  };

  const handleBodySave = (): void => {
    updateMutation.mutate({
      body: bodyText,
    });
  };

  const statusColors: Record<DocumentStatus, string> = { draft: 'default', sent: 'blue', signed: 'green', cancelled: 'red' };
  const statusLabels: Record<DocumentStatus, string> = { draft: 'Черновик', sent: 'Отправлен', signed: 'Подписан', cancelled: 'Отменен' };

  return (
    <ConfigProvider theme={{ token: { borderRadius: 6, colorPrimary: '#0f172a', colorBorder: '#e5e7eb', colorBgLayout: '#f5f5f4' } }}>
      <main className="flex h-full w-full flex-col overflow-hidden bg-stone-50">
        <header className="bg-white flex-none px-6 py-4 border-b border-gray-200">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <Link
                to="/documents"
                className="mb-2 inline-flex items-center gap-2 text-xs font-semibold tracking-widest text-gray-500 uppercase hover:text-gray-900"
              >
                <ArrowLeftOutlined />
                К списку документов
              </Link>
              <div className="flex flex-wrap items-center gap-3 mt-1">
                <h1 className="text-xl font-semibold text-gray-900 m-0">{document.title}</h1>
                <Tag color={statusColors[document.status]}>{statusLabels[document.status]}</Tag>
                {document.contact && (
                  <span className="rounded border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600">
                    {document.contact.firstName} {document.contact.lastName || ''}
                  </span>
                )}
                {document.deal && (
                  <span className="rounded border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600">
                    Сделка: {document.deal.title}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button type="primary" onClick={handleBodySave} loading={updateMutation.isPending} icon={<SaveOutlined />}>
                Сохранить
              </Button>
            </div>
          </div>
        </header>

        <div className="flex flex-1 min-h-0">
          <aside className="flex-none border-r border-gray-200 bg-white overflow-y-auto" style={{ width: 320 }}>
            <div className="flex flex-col h-full bg-white">
              <div className="border-b border-gray-200 px-5 py-3 flex-none">
                <span className="text-xs font-semibold tracking-wider text-gray-900 uppercase">Детали</span>
              </div>
              <div className="px-5 py-5 flex-1 overflow-y-auto">
                <Form
                  key={`${document.id}:${document.updatedAt}:details`}
                  layout="vertical"
                  initialValues={{
                    title: document.title,
                    type: document.type,
                    status: document.status,
                    amount: document.amount ? Number(document.amount) : null,
                    contactId: document.contactId,
                    dealId: document.dealId,
                  }}
                  onFinish={handleDetailsSave}
                  className="flex flex-col gap-1"
                >
                  <div className="group px-2 py-1.5 -mx-2 rounded hover:bg-gray-50 transition-colors">
                    <Form.Item label={<span className="text-xs font-medium text-gray-500 uppercase tracking-widest">Название</span>} name="title" style={{ marginBottom: 0 }}>
                      <Input size="small" variant="borderless" className="px-0 py-0 text-sm text-gray-900 font-medium focus:bg-white focus:ring-1 focus:ring-slate-900 transition-all rounded-sm" />
                    </Form.Item>
                  </div>
                  
                  <div className="group px-2 py-1.5 -mx-2 rounded hover:bg-gray-50 transition-colors">
                    <Form.Item label={<span className="text-xs font-medium text-gray-500 uppercase tracking-widest">Тип</span>} name="type" style={{ marginBottom: 0 }}>
                      <Select size="small" variant="borderless" className="w-full">
                        <Select.Option value="contract">Договор</Select.Option>
                        <Select.Option value="invoice">Счет</Select.Option>
                        <Select.Option value="act">Акт</Select.Option>
                      </Select>
                    </Form.Item>
                  </div>

                  <div className="group px-2 py-1.5 -mx-2 rounded hover:bg-gray-50 transition-colors">
                    <Form.Item label={<span className="text-xs font-medium text-gray-500 uppercase tracking-widest">Статус</span>} name="status" style={{ marginBottom: 0 }}>
                      <Select size="small" variant="borderless" className="w-full">
                        <Select.Option value="draft">Черновик</Select.Option>
                        <Select.Option value="sent">Отправлен</Select.Option>
                        <Select.Option value="signed">Подписан</Select.Option>
                        <Select.Option value="cancelled">Отменен</Select.Option>
                      </Select>
                    </Form.Item>
                  </div>

                  <div className="group px-2 py-1.5 -mx-2 rounded hover:bg-gray-50 transition-colors">
                    <Form.Item label={<span className="text-xs font-medium text-gray-500 uppercase tracking-widest">Сумма</span>} name="amount" style={{ marginBottom: 0 }}>
                      <InputNumber size="small" variant="borderless" className="w-full px-0 py-0 text-sm text-gray-900 font-medium focus:bg-white focus:ring-1 focus:ring-slate-900 transition-all rounded-sm" min={0} />
                    </Form.Item>
                  </div>

                  <div className="group px-2 py-1.5 -mx-2 rounded hover:bg-gray-50 transition-colors">
                    <Form.Item label={<span className="text-xs font-medium text-gray-500 uppercase tracking-widest">Контакт</span>} name="contactId" style={{ marginBottom: 0 }}>
                      <Select size="small" variant="borderless" allowClear className="w-full">
                        {contacts?.map((c: ContactItem) => (
                          <Select.Option key={c.id} value={c.id}>{c.fullName}</Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>

                  <div className="group px-2 py-1.5 -mx-2 rounded hover:bg-gray-50 transition-colors">
                    <Form.Item label={<span className="text-xs font-medium text-gray-500 uppercase tracking-widest">Сделка</span>} name="dealId" style={{ marginBottom: 0 }}>
                      <Select size="small" variant="borderless" allowClear className="w-full">
                        {deals?.map((d: DealListItem) => (
                          <Select.Option key={d.id} value={d.id}>{d.title}</Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>

                  <Button type="default" size="small" style={{ marginTop: 16 }} htmlType="submit" loading={updateMutation.isPending}>
                    Сохранить детали
                  </Button>
                </Form>
              </div>
            </div>
          </aside>

          <section className="flex-1 min-w-0 bg-[#f9fafb] overflow-y-auto px-10 py-8 flex flex-col items-center">
            <div className="bg-white rounded shadow-sm border border-gray-200 w-full max-w-3xl min-h-[800px] flex flex-col overflow-hidden">
               <div className="border-b border-gray-200 px-5 py-3 bg-gray-50">
                 <Text type="secondary" className="text-xs uppercase tracking-widest">Текст документа</Text>
               </div>
               <Input.TextArea
                 value={bodyText}
                 onChange={(e) => setBodyText(e.target.value)}
                 className="flex-1 w-full h-full border-0 focus:ring-0 resize-none p-8"
                 style={{ fontSize: 15, lineHeight: 1.6 }}
                 placeholder="Начните писать текст документа или попросите AI сгенерировать его..."
               />
            </div>
          </section>

          <aside className="flex-none border-l border-gray-200 bg-white flex flex-col min-h-0" style={{ width: 340 }}>
            <div className="border-b border-gray-200 px-5 py-3 flex-none flex items-center justify-between">
              <span className="text-xs font-semibold tracking-wider text-gray-900 uppercase">AI Помощник</span>
              <span className="text-xs uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded font-bold">Готов</span>
            </div>
            <div className="flex-1 min-h-0 relative overflow-hidden">
              <DealChatPanel scope={scope} dealId={document.dealId ?? ''} />
            </div>
          </aside>
        </div>
      </main>
    </ConfigProvider>
  );
}
